import type { FieldSchema, SettingsRow, WorkflowStep } from '@/api/types'

// Helpers shared by the settings table and its row form: default values per field type,
// per-field validation and the conversions between JSON row values and form inputs.

const TIME = /^(\d+\.)?\d{1,2}:\d{2}:\d{2}$/

export function defaultValue(field: FieldSchema): unknown {
  switch (field.type) {
    case 'integer':
      return 0
    case 'boolean':
      return false
    case 'time':
      return '00:00:00'
    case 'enum':
      return field.values?.[0] ?? ''
    case 'menum':
      return []
    case 'checkbox':
      return Object.fromEntries((field.checkboxKeys ?? []).map((k) => [k, false]))
    case 'workflow':
      return []
    default:
      return ''
  }
}

export function emptyRow(fields: FieldSchema[]): SettingsRow {
  return Object.fromEntries(fields.map((f) => [f.name, defaultValue(f)]))
}

export function validateField(field: FieldSchema, value: unknown): string | null {
  const label = fieldLabel(field)
  if (field.type === 'integer') {
    if (value === '' || value === null || value === undefined) return field.isRequired ? `${label} is required` : null
    if (!Number.isInteger(Number(value))) return `${label} must be a whole number`
    if (field.isKey && Number(value) <= 0) return `${label} must be bigger than 0`
    return null
  }
  if (field.type === 'time') return typeof value === 'string' && TIME.test(value) ? null : `${label} must look like 00:05:00`
  if (field.type === 'workflow') {
    const steps = (value as WorkflowStep[] | undefined) ?? []
    return steps.every((s) => s.requesterType && s.proxyConfigurations.every((p) => p.proxyProduct)) ? null : 'Every workflow step needs a requester and a proxy product'
  }
  if (field.isRequired && (value === '' || value === null || value === undefined)) return `${label} is required`
  return null
}

export function validateRow(fields: FieldSchema[], row: SettingsRow): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const field of fields) {
    const error = validateField(field, row[field.name])
    if (error) errors[field.name] = error
  }
  return errors
}

/** Values typed in inputs are strings; coerce them back to what the API expects. */
export function normaliseRow(fields: FieldSchema[], row: SettingsRow): SettingsRow {
  const result: SettingsRow = { ...row }
  for (const field of fields) {
    const value = row[field.name]
    if (field.type === 'integer') result[field.name] = value === '' || value === null || value === undefined ? 0 : Number(value)
    else if (field.type === 'menum') result[field.name] = Array.isArray(value) ? value : typeof value === 'string' && value ? value.split(',') : []
    else if (field.type === 'string' || field.type === 'textarea') result[field.name] = value === '' ? null : value
  }
  return result
}

/** Short text for a cell in the rows table. */
export function displayValue(field: FieldSchema, value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  switch (field.type) {
    case 'boolean':
      return value ? 'yes' : 'no'
    case 'menum':
      return Array.isArray(value) ? value.join(', ') : String(value)
    case 'checkbox':
      return Object.entries((value as Record<string, boolean>) ?? {})
        .filter(([, on]) => on)
        .map(([k]) => k)
        .join(', ')
    case 'workflow':
      return Array.isArray(value) ? `${value.length} step${value.length === 1 ? '' : 's'}` : ''
    default:
      return String(value)
  }
}

/** Identifier the API expects on DELETE: the value of the first key field. */
export function rowId(fields: FieldSchema[], row: SettingsRow): string {
  const key = fields.find((f) => f.isKey)
  return String(key ? (row[key.name] ?? '') : '')
}

/** "isSelectableUI" → "Is selectable UI" for fields without a display name. */
export function fieldLabel(field: FieldSchema): string {
  if (field.displayName) return field.displayName
  const words = field.name.replace(/([a-z\d])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
  return words.charAt(0).toUpperCase() + words.slice(1).replace(/\b[A-Z][a-z]+\b/g, (w) => w.toLowerCase())
}
